import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import {
	getSettlementAdjustments,
	getSettlementItems,
	getAdjustmentDetail,
	SettlementAdjustment,
	SettlementItem,
	formatNumber,
} from "../../../../services/SettlementService";

interface BookingDetailModalProps {
	isOpen: boolean;
	onClose: () => void;
	onApprove: (id: string) => void;
	onReject: (id: string) => void;
	bookingId?: string;
	bookingItemId?: number;
	bookingItem?: SettlementItem;
	cycleId?: number;
	currency?: string;
}

const ModalOverlay = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 999999;
	padding: 20px;
`;

const ModalContent = styled.div`
	background: white;
	border-radius: 5px;
	width: 1100px;
	max-width: calc(100vw - 40px);
	max-height: calc(100vh - 40px);
	overflow-y: auto;
	position: relative;
`;

const ModalHeader = styled.div`
	padding: 20px 20px 0 20px;
	display: flex;
	justify-content: space-between;
	align-items: center;
`;

const ModalTitle = styled.h2`
	font-size: 16px;
	font-weight: 700;
	color: #000;
	margin: 0;
`;

const CloseButton = styled.button`
	background: none;
	border: none;
	font-size: 24px;
	color: #6b7280;
	cursor: pointer;
	padding: 4px;

	&:hover {
		color: #374151;
	}
`;

const ModalBody = styled.div`
	padding: 20px;
`;

const BookingSummary = styled.div`
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 16px;
	margin-bottom: 24px;
`;

const SummaryCard = styled.div`
	background-color: #dbeafe;
	border: 1px solid #c0bfbf;
	border-radius: 1px;
	padding: 16px;
	text-align: left;
`;

const SummaryLabel = styled.div`
	font-size: 12px;
	color: #6b7280;
	margin-bottom: 8px;
	font-weight: 500;
`;

const SummaryValue = styled.div`
	font-size: 16px;
	font-weight: 700;
	color: #111827;
`;

const TransactionSection = styled.div`
	margin-top: 24px;
`;

const SectionTitle = styled.h3`
	font-size: 16px;
	font-weight: 600;
	color: #111827;
	margin-bottom: 16px;
`;

const TransactionTable = styled.div`
	overflow: hidden;
`;

const TableHeader = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
	gap: 16px;
	padding: 16px;
	border-bottom: 1px solid #e5e7eb;
	font-size: 12px;
	font-weight: 600;
	color: #6b7280;
`;

const TableRow = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
	gap: 16px;
	padding: 16px;
	border-bottom: 1px solid #e5e7eb;

	&:last-child {
		border-bottom: none;
	}
`;

const TableCell = styled.div`
	font-size: 14px;
	color: #374151;
	display: flex;
	align-items: center;
`;

const StatusTag = styled.div<{ backgroundColor: string; textColor: string }>`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	background-color: ${(props) => props.backgroundColor};
	color: ${(props) => props.textColor};
	padding: 6px 12px;
	border-radius: 20px;
	font-size: 12px;
	font-weight: 500;
	width: fit-content;
	border: 1px solid ${(props) => props.textColor};
`;

const Dot = styled.div<{ color: string }>`
	width: 6px;
	height: 6px;
	border-radius: 50%;
	background-color: ${(props) => props.color};
`;

const ActionButton = styled.button<{ variant: "approve" | "reject" }>`
	padding: 1px 10px;
	border-radius: 4px;
	font-size: 12px;
	font-weight: 500;
	cursor: pointer;
	margin-right: 8px;
	white-space: nowrap;

	${(props) => {
		switch (props.variant) {
			case "approve":
				return `
					background: #17B26A;
					color: white;
					border: 1px solid #17B26A;
					
					&:hover {
						background: #17B26A;
					}
				`;
			case "reject":
				return `
					background: #FF0000;
					color: white;
					border: 1px solid #FF0000;
					
					&:hover {
						background: #FF0000;
					}
				`;
		}
	}}
`;

const ModalFooter = styled.div`
	padding: 0 20px 20px 20px;
	display: flex;
	justify-content: flex-end;
`;

const CloseButtonFooter = styled.button`
	background: #fff;
	color: white;
	border: 1px solid #6b7280;
	color: #6b7280;
	padding: 10px 20px;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
`;

export default function BookingDetailModal({
	isOpen,
	onClose,
	onApprove,
	onReject,
	bookingId = "BKG00001",
	bookingItemId,
	bookingItem,
	cycleId,
	currency = "USD",
}: BookingDetailModalProps) {
	const [loading, setLoading] = useState(false);
	const [adjustments, setAdjustments] = useState<SettlementAdjustment[]>([]);
	const [bookingDetail, setBookingDetail] = useState<SettlementItem | null>(null);

	// Load booking detail và adjustments
	useEffect(() => {
		if (!isOpen || !bookingItemId || !cycleId) {
			setAdjustments([]);
			setBookingDetail(null);
			return;
		}

		const loadData = async () => {
			setLoading(true);
			try {
				let currentBookingDetail: SettlementItem | null = null;

				// Load booking item detail nếu chưa có
				if (bookingItem) {
					currentBookingDetail = bookingItem as SettlementItem;
					setBookingDetail(bookingItem as SettlementItem);
				} else if (bookingItemId) {
					// Load từ API nếu chưa có data
					const response = await getSettlementItems(
						1,
						100,
						cycleId,
						undefined,
						undefined,
						undefined,
						undefined,
						{ id: "DESC" }
					);
					const item = response.data.find((item: SettlementItem) => item.id === bookingItemId);
					if (item) {
						currentBookingDetail = item;
						setBookingDetail(item);
					}
				}

				// Load adjustments liên quan đến booking item (filter theo orderId)
				if (currentBookingDetail?.orderId) {
					try {
						const adjResponse = await getSettlementAdjustments(1, 100, cycleId);
						let relatedAdjustments = adjResponse.data.filter(
							(adj: SettlementAdjustment) => adj.orderId === currentBookingDetail!.orderId
						);

						// Load detail cho các adjustment bị từ chối để có đầy đủ reasonRefusal
						// API list có thể không trả về reasonRefusal, nên cần load detail
						const adjustmentsWithDetails = await Promise.all(
							relatedAdjustments.map(async (adj: SettlementAdjustment) => {
								// Nếu adjustment bị từ chối (status = 40), luôn load detail để đảm bảo có reasonRefusal
								if (adj.status === 40) {
									try {
										const detail = await getAdjustmentDetail(adj.id);
										return detail;
									} catch (error) {
										console.error(`Error loading adjustment detail ${adj.id}:`, error);
										return adj; // Fallback to original if detail load fails
									}
								}
								return adj;
							})
						);

						setAdjustments(adjustmentsWithDetails);
					} catch (error) {
						console.error("Error loading adjustments:", error);
						setAdjustments([]);
					}
				} else {
					setAdjustments([]);
				}
			} catch (error) {
				console.error("Error loading booking detail:", error);
			} finally {
				setLoading(false);
			}
		};

		loadData();
	}, [isOpen, bookingItemId, cycleId, bookingItem]);

	// Format date
	const formatDate = (dateString: string | null) => {
		if (!dateString) return "-";
		const date = new Date(dateString);
		return date.toLocaleDateString("vi-VN", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	// Get actor info từ adjustment
	const getActorInfo = (adjustment: SettlementAdjustment) => {
		if (adjustment.proposer?.role === 1) {
			return { name: "Whalelo", color: "#1E40AF", bg: "#DBEAFE" };
		} else if (adjustment.proposer?.role === 2) {
			return { name: "Station", color: "#059669", bg: "#ECFDF3" };
		}
		return { name: "System", color: "#B45309", bg: "#FEF3C7" };
	};

	// Get status info
	// SettlementItemStatus: CyclePending = 10, AdjProposed = 20, Confirm = 30, Reject = 40
	// SettlementAdjustmentStatus: Pending = 10, Confirm = 20, Cancel = 30, Reject = 40
	const getStatusInfo = (status: number) => {
		switch (status) {
			case 10: // CyclePending / Pending
				return { text: "Chờ xử lý", color: "#864516", bg: "#FEF9C3", hasActions: false };
			case 20: // AdjProposed - Chờ duyệt / Confirm
				return { text: "Chờ duyệt", color: "#864516", bg: "#FEF9C3", hasActions: true };
			case 30: // Confirm - Đã duyệt / Cancel
				return { text: "Đã duyệt", color: "#067647", bg: "#ECFDF3", hasActions: false };
			case 40: // Reject - Đã từ chối
				return { text: "Đã từ chối", color: "#B42318", bg: "#FEF3F2", hasActions: false };
			default:
				return { text: "Không xác định", color: "#9A5757", bg: "#FFEDD5", hasActions: false };
		}
	};

	// Tạo transaction data từ adjustments và booking item (chỉ từ data thật)
	const transactionData = React.useMemo(() => {
		const data: any[] = [];

		// Chỉ thêm booking item gốc nếu có data thật
		if (bookingDetail && !loading) {
			const statusInfo = getStatusInfo(bookingDetail.status || 20);
			// SettlementItemType: Base = 10, Adjustment = 20
			const itemTypeText =
				bookingDetail.type === 10 ? "Cơ bản" : bookingDetail.type === 20 ? "Điều chỉnh" : "Không xác định";

			// Tìm lý do từ chối từ adjustments liên quan nếu status là Reject
			let rejectReason = null;
			if (bookingDetail.status === 40 && adjustments && adjustments.length > 0) {
				// Tìm adjustment bị từ chối liên quan đến booking này
				const rejectedAdjustment = adjustments.find(
					(adj: any) => adj.status === 40 && adj.orderId === bookingDetail.orderId && adj.reasonRefusal
				);
				if (rejectedAdjustment) {
					rejectReason = rejectedAdjustment.reasonRefusal;
				}
			}

			data.push({
				id: String(bookingDetail.id),
				time: formatDate(bookingDetail.createdAt || bookingDetail.timeIn),
				actor: "System",
				actorColor: "#B45309",
				actorBg: "#FEF3C7",
				value: `+${formatNumber(bookingDetail.finalAmount || 0)} ${currency}`,
				valueColor: "#10B981",
				reason: itemTypeText,
				status: statusInfo.text,
				statusColor: statusInfo.color,
				statusBg: statusInfo.bg,
				hasActions: statusInfo.hasActions, // Chỉ hiển thị nút nếu status = 20 (AdjProposed)
				rejectReason: rejectReason,
			});
		}

		// Chỉ thêm adjustments nếu có data thật
		if (adjustments && adjustments.length > 0 && !loading) {
			adjustments.forEach((adj) => {
				const actorInfo = getActorInfo(adj);
				const statusInfo = getStatusInfo(adj.status);
				const amount = adj.amount || 0;
				// SettlementAdjustmentType: Increase = 10, Decrease = 20
				const isPositive = adj.type === 10; // Increase là positive

				data.push({
					id: String(adj.id),
					time: formatDate(adj.createdAt),
					actor: actorInfo.name,
					actorColor: actorInfo.color,
					actorBg: actorInfo.bg,
					value: `${isPositive ? "+" : "-"}${formatNumber(Math.abs(amount))} ${currency}`,
					valueColor: isPositive ? "#10B981" : "#EF4444",
					reason: adj.reasonCode || adj.description || "-",
					status: statusInfo.text,
					statusColor: statusInfo.color,
					statusBg: statusInfo.bg,
					hasActions: statusInfo.hasActions && adj.status === 10,
					rejectReason: adj.reasonRefusal || null,
				});
			});
		}

		// Sort theo thời gian (mới nhất trước)
		data.sort((a, b) => {
			const timeA = new Date(a.time).getTime();
			const timeB = new Date(b.time).getTime();
			return timeB - timeA;
		});

		return data;
	}, [bookingDetail, adjustments, loading]);

	if (!isOpen) return null;

	const modalContent = (
		<ModalOverlay>
			<ModalContent onClick={(e) => e.stopPropagation()}>
				<ModalHeader>
					<ModalTitle>Chi tiết Booking #{bookingId}</ModalTitle>
					<CloseButton onClick={onClose}>×</CloseButton>
				</ModalHeader>

				<ModalBody>
					{loading ? (
						<div style={{ padding: "40px", textAlign: "center" }}>Đang tải...</div>
					) : (
						<>
							<BookingSummary>
								<SummaryCard>
									<SummaryLabel>Booking ID</SummaryLabel>
									<SummaryValue>{bookingDetail?.id || bookingId || "-"}</SummaryValue>
								</SummaryCard>
								<SummaryCard>
									<SummaryLabel>Khách hàng</SummaryLabel>
									<SummaryValue>
										<div>{bookingDetail?.customerName || "-"}</div>
										{bookingDetail?.customerEmail && (
											<div style={{ fontSize: "12px", color: "#6B7280", marginTop: "4px" }}>
												{bookingDetail.customerEmail}
											</div>
										)}
									</SummaryValue>
								</SummaryCard>
								<SummaryCard>
									<SummaryLabel>Số hành lý</SummaryLabel>
									<SummaryValue>{bookingDetail?.quantity || 0} bag</SummaryValue>
								</SummaryCard>
								<SummaryCard>
									<SummaryLabel>Tổng tiền</SummaryLabel>
									<SummaryValue style={{ color: "#2563EB" }}>
										{bookingDetail?.finalAmount
											? `${formatNumber(bookingDetail.finalAmount)} ${currency}`
											: "-"}
									</SummaryValue>
								</SummaryCard>
							</BookingSummary>
						</>
					)}

					<TransactionSection>
						<TransactionTable>
							<TableHeader>
								<TableCell>THỜI GIAN</TableCell>
								<TableCell>NGƯỜI TÁC ĐỘNG</TableCell>
								<TableCell>GIÁ TRỊ</TableCell>
								<TableCell>LÝ DO</TableCell>
								<TableCell>TRẠNG THÁI</TableCell>
								{/* <TableCell>LÝ DO TỪ CHỐI</TableCell> */}
								<TableCell>HÀNH ĐỘNG</TableCell>
							</TableHeader>
							{loading ? (
								<TableRow>
									<div style={{ textAlign: "center", padding: "20px", gridColumn: "1 / -1" }}>
										Đang tải dữ liệu...
									</div>
								</TableRow>
							) : !bookingDetail && !loading ? (
								<TableRow>
									<div style={{ textAlign: "center", padding: "20px", gridColumn: "1 / -1" }}>
										Không tìm thấy dữ liệu booking
									</div>
								</TableRow>
							) : transactionData.length === 0 ? (
								<TableRow>
									<div style={{ textAlign: "center", padding: "20px", gridColumn: "1 / -1" }}>
										Không có dữ liệu
									</div>
								</TableRow>
							) : (
								transactionData.map((item) => (
									<TableRow key={item.id}>
										<TableCell>{item.time}</TableCell>
										<TableCell>
											<StatusTag
												backgroundColor={item.actorBg}
												textColor={item.actorColor}>
												<Dot color={item.actorColor} />
												{item.actor}
											</StatusTag>
										</TableCell>
										<TableCell style={{ color: item.valueColor, fontWeight: "500" }}>
											{item.value}
										</TableCell>
										<TableCell>{item.reason}</TableCell>
										<TableCell>
											<StatusTag
												backgroundColor={item.statusBg}
												textColor={item.statusColor}>
												<Dot color={item.statusColor} />
												{item.status}
											</StatusTag>
										</TableCell>
										{/* <TableCell>
										{item.rejectReason ? (
											<span style={{color: '#B42318', fontSize: '14px'}}>{item.rejectReason}</span>
										) : (
											<span style={{color: '#9CA3AF'}}>-</span>
										)}
									</TableCell> */}
										<TableCell>
											{item.hasActions ? (
												<>
													<ActionButton
														variant="approve"
														onClick={() => onApprove(item.id)}>
														Duyệt
													</ActionButton>
													<ActionButton
														variant="reject"
														onClick={() => onReject(item.id)}>
														Từ chối
													</ActionButton>
												</>
											) : (
												<span style={{ color: "#9CA3AF" }}>-</span>
											)}
										</TableCell>
									</TableRow>
								))
							)}
						</TransactionTable>
					</TransactionSection>
				</ModalBody>

				<ModalFooter>
					<CloseButtonFooter onClick={onClose}>Đóng</CloseButtonFooter>
				</ModalFooter>
			</ModalContent>
		</ModalOverlay>
	);

	return createPortal(modalContent, document.body);
}
